import type { HTMLAttributes } from "react";
import { CircleHelp, Info, ShieldCheck, Sigma } from "lucide-react";
import { cn } from "../../lib/cn";
import { formatPercent } from "../../lib/format";
import { Badge } from "../Badge";
import { Tooltip } from "../Tooltip";
import type { BadgeTone } from "../Badge";

/**
 * Grau de confiança da ATRIBUIÇÃO (docs/product/métricas.md seção 3 e
 * docs/integrations/meta-ads-e-atribuição.md).
 *
 * O WhatsApp não devolve NADA do clique: o casamento clique -> entrada é
 * probabilístico. Esse badge existe para o painel nunca vender estimativa
 * como certeza — todo número atribuído carrega o grau de confiança junto,
 * na tela, no CSV e na API.
 *
 *  deterministic — casamento com prova (clique único na janela, sem ambiguidade)
 *  probable      — score >= limiar, com `score` exposto ao lado
 *  unattributed  — sem clique na janela, ou score abaixo do limiar.
 *                  NÃO some do total geral: ele soma e fica marcado.
 */
export type AttributionConfidence = "deterministic" | "probable" | "unattributed";

const SPEC: Record<
  AttributionConfidence,
  { label: string; tone: BadgeTone; icon: typeof Info; explain: string }
> = {
  deterministic: {
    label: "Determinístico",
    tone: "success",
    icon: ShieldCheck,
    explain:
      "Entrada casada com um clique único dentro da janela de atribuição, sem candidato concorrente.",
  },
  probable: {
    label: "Provável",
    tone: "info",
    icon: Sigma,
    explain:
      "Casamento probabilístico entre clique e entrada. O WhatsApp não devolve dado do clique — este número é uma estimativa por baixo, nunca extrapolada sobre a fatia não atribuída.",
  },
  unattributed: {
    label: "Não atribuído",
    tone: "neutral",
    icon: CircleHelp,
    explain:
      "Sem clique na janela, ou confiança abaixo do limiar. Continua somando no total geral, mas não entra em CPE nem em desempenho de campanha.",
  },
};

export interface ConfidenceBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  confidence: AttributionConfidence;
  /** Score 0..1 do casamento. Só faz sentido em `probable`. */
  score?: number | null;
  /**
   * Cobertura de atribuição do recorte (`entradas_atribuídas / Entradas`).
   * Quando presente, entra no tooltip: um CPE bonito com 40% de cobertura
   * é um número falso, e o usuário tem que ver isso junto.
   */
  coverage?: number | null;
  /**
   * `"badge"` (padrão) — pílula com ícone + rótulo, para linha de lista.
   * `"inline"` — só o `ⓘ` de 12px ao lado de um número (seção 7), para
   *              marcar a incerteza sem roubar a atenção do valor.
   */
  variant?: "badge" | "inline";
  /** Link "ver metodologia" que entra no fim do tooltip. */
  methodologyHint?: string;
}

export function ConfidenceBadge({
  confidence,
  score,
  coverage,
  variant = "badge",
  methodologyHint = "Veja a metodologia em Ajustes › Atribuição.",
  className,
  ...props
}: ConfidenceBadgeProps) {
  const spec = SPEC[confidence];
  const Icon = spec.icon;

  const hasScore =
    confidence === "probable" && score !== null && score !== undefined && Number.isFinite(score);

  const hasCoverage = coverage !== null && coverage !== undefined && Number.isFinite(coverage);

  const label = hasScore ? `${spec.label} · ${formatPercent(score as number, 0)}` : spec.label;

  const tooltip = (
    <span className="block space-y-1">
      {/* dentro da bolha INVERSA: hierarquia por opacidade, não por cor de tema */}
      <span className="block font-semibold">{label}</span>
      <span className="block">{spec.explain}</span>
      {hasCoverage ? (
        <span className="block opacity-75">
          Cobertura de atribuição no período: {formatPercent(coverage as number, 0)}.
        </span>
      ) : null}
      <span className="block opacity-75">{methodologyHint}</span>
    </span>
  );

  if (variant === "inline") {
    return (
      <Tooltip content={tooltip} side="top">
        <span
          tabIndex={0}
          role="note"
          aria-label={`Atribuição: ${label}`}
          className={cn(
            "inline-flex cursor-help items-center rounded-full text-subtle-foreground align-middle",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            className,
          )}
          {...props}
        >
          <Info aria-hidden="true" className="size-3.25" />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltip} side="top">
      <Badge
        tone={spec.tone}
        icon={<Icon />}
        tabIndex={0}
        aria-label={`Atribuição: ${label}`}
        className={cn("cursor-help", hasScore && "nums", className)}
        {...props}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}
